import axios from 'axios';
import { normalizeAxiosErrorDetail, parseBlobErrorData } from '@/lib/apiError';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Autenticação por cookies httpOnly — os cookies têm de viajar nos pedidos.
  withCredentials: true,
});

// Lê um cookie pelo nome (usado para o token CSRF, que é legível por JS).
function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Double-submit CSRF: reenvia o cookie csrf_token no cabeçalho X-CSRF-Token
// para pedidos mutáveis (o backend valida a coincidência).
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Em 401, tenta renovar a sessão uma vez (cookie refresh_token) e repete o
// pedido original; se a renovação falhar, redirecciona para o login.
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    await parseBlobErrorData(error);
    normalizeAxiosErrorDetail(error);

    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || '';
    const isAuthFlow = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (status === 401 && original && !original._retried && !isAuthFlow) {
      original._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh').finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        return api(original);
      } catch (_refreshErr) {
        localStorage.removeItem('user');
        // Só forçamos o redirect quando estamos numa rota protegida — páginas
        // públicas (login/forgot/reset) não devem ser interrompidas pelo probe
        // inicial /auth/me que falha por não haver sessão.
        const publicPaths = ['/login', '/forgot-password', '/reset-password'];
        if (
          typeof window !== 'undefined' &&
          !publicPaths.includes(window.location.pathname)
        ) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============
// `identifier` aceita email OU username — backend resolve por qualquer um
// dos dois (case-insensitive no email). Mantemos `username` no payload por
// retro-compatibilidade caso o backend antigo ainda esteja em produção.
export const authAPI = {
  login: (identifier, password) =>
    api.post('/auth/login', { identifier, username: identifier, password }),
  refresh: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) =>
    api.post('/auth/reset-password', { token, new_password }),
};

// ============ RFQS ============
export const rfqAPI = {
  list: (params = {}) => api.get('/rfqs', { params }),
  get: (id) => api.get(`/rfqs/${id}`),
  create: (data) => api.post('/rfqs', data),
  update: (id, data) => api.put(`/rfqs/${id}`, data),
  remove: (id) => api.delete(`/rfqs/${id}`),
  duplicate: (id) => api.post(`/rfqs/${id}/duplicate`),
  // Workflow — business gates
  strategicReject: (id, comment) =>
    api.post(`/rfqs/${id}/strategic-reject`, { comment }),
  requestClientInfo: (id, text, options = {}) =>
    api.post(`/rfqs/${id}/request-client-info`, {
      text,
      send_email: options.send_email !== undefined ? options.send_email : true,
      recipient_role: options.recipient_role || null,
      recipients: options.recipients || null,
      cc: options.cc || null,
    }),
  markClientInfoReceived: (id) =>
    api.post(`/rfqs/${id}/mark-client-info-received`),
  submitToEngineering: (id) => api.post(`/rfqs/${id}/submit-to-engineering`),
  assignEngineer: (id, engineeringResponsible) =>
    api.post(`/rfqs/${id}/assign-engineer`, { engineering_responsible: engineeringResponsible }),
  returnToCommercial: (id, comment) =>
    api.post(`/rfqs/${id}/return-to-commercial`, { comment }),
  // Item 10 / WF-01: Engenharia → Comercial (margens) directamente. O fluxo
  // legacy (Logística / Financeiro) foi removido.
  submitToCommercialMargins: (id, risk_class, comment = '') =>
    api.post(`/rfqs/${id}/submit-to-commercial-margins`, { risk_class, comment }),
  // Item 10a/b: decisão automática (APPROVED ou Administração G5) com base
  // no PnL audit cross-check. Notifica Administração quando o PV teórico
  // Schreder fica negativo (aviso, sem bloquear).
  submitFinalDecision: (id, comment = '') =>
    api.post(`/rfqs/${id}/submit-final-decision`, { comment }),
  validate: (id, data) => api.post(`/rfqs/${id}/validate`, data),
  markOfferSent: (id) => api.post(`/rfqs/${id}/mark-offer-sent`),
  updateTracking: (id, data) => api.post(`/rfqs/${id}/tracking`, data),
  // Item 11: reabrir RFQ que está em negociação para nova versão. O fluxo
  // volta a IN_PREPARATION (Comercial edita) e tem que passar de novo por
  // Engenharia antes de poder fechar como Won/Lost.
  reopenFromNegotiation: (id, comment = '') =>
    api.post(`/rfqs/${id}/reopen-from-negotiation`, { comment }),
  getHistory: (id) => api.get(`/rfqs/${id}/history`),
  getValidations: (id) => api.get(`/rfqs/${id}/validations`),
  // Documents
  uploadDocument: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/rfqs/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Upload no slot G1 (model_3d | drawing_2d | additional). Substitui o documento
  // anterior do slot. Quando slot==='drawing_2d' e PDF, dispara extracção em-linha.
  uploadDocumentToSlot: (id, slot, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/rfqs/${id}/documents/slot/${slot}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (rfqId, documentId) =>
    api.delete(`/rfqs/${rfqId}/documents/${documentId}`),
  listDocuments: (id) => api.get(`/rfqs/${id}/documents`),
  downloadDocument: (rfqId, documentId) =>
    api.get(`/rfqs/${rfqId}/documents/${documentId}`, { responseType: 'blob' }).then((res) => res.data),
  // Extracção do desenho 2D (modo descoberta — só listagem, não auto-preenche).
  getDrawingExtraction: (rfqId, documentId) =>
    api.get(`/rfqs/${rfqId}/documents/${documentId}/extraction`),
  reextractDrawing: (rfqId, documentId) =>
    api.post(`/rfqs/${rfqId}/documents/${documentId}/re-extract`),
  // Notes
  addNote: (id, data) => api.post(`/rfqs/${id}/notes`, data),
  listNotes: (id) => api.get(`/rfqs/${id}/notes`),
  // Form-level versioning
  listVersions: (id) => api.get(`/rfqs/${id}/versions`),
  getVersion: (id, versionId) => api.get(`/rfqs/${id}/versions/${versionId}`),
  unlockForEdit: (id) => api.post(`/rfqs/${id}/unlock-for-edit`),
  lockEdit: (id, comment = '') => api.post(`/rfqs/${id}/lock-edit`, { comment }),
};

// ============ OFFERS (PDF) ============
// Helper local — converte string vazia / undefined em null e o resto em Number
// inteiro. Aplica-se ao validity_days e às semanas de planning, todas elas
// `Optional[int]` no backend.
const _intOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const offerAPI = {
  list: (rfqId) => api.get(`/rfqs/${rfqId}/offers`),
  // ``data`` aceita: observations, validity_days, specific_conditions, os
  // 2 payment terms e os 4 campos opcionais de planning (semanas)
  // editáveis no diálogo "Gerar Offer (PDF)". Quando os campos de planning
  // vêm vazios, o backend cai sobre os defaults da configuração global
  // (12 / 2 / 8 / 6); os payment terms vazios herdam os do cliente e, em
  // última instância, os defaults globais.
  generate: (rfqId, data = {}) =>
    api.post(`/rfqs/${rfqId}/offer/generate`, {
      observations: data.observations || '',
      validity_days: _intOrNull(data.validity_days),
      specific_conditions: data.specific_conditions || '',
      payment_terms_parts: data.payment_terms_parts || '',
      payment_terms_tooling: data.payment_terms_tooling || '',
      planning_first_article_weeks: _intOrNull(data.planning_first_article_weeks),
      planning_first_series_weeks: _intOrNull(data.planning_first_series_weeks),
      planning_supplying_weeks: _intOrNull(data.planning_supplying_weeks),
      planning_supplying_forecast_weeks: _intOrNull(data.planning_supplying_forecast_weeks),
      language: data.language || 'en',
    }),
  download: (rfqId, offerId) =>
    api
      .get(`/rfqs/${rfqId}/offer/${offerId}/download`, { responseType: 'blob' })
      .then((res) => res.data),
};

// ============ QUOTATION ============
export const quotationAPI = {
  // Barra final obrigatória: o backend define a rota em "/" (caminho canónico
  // com barra). Sem ela, o FastAPI responde 307 e o browser repete o pedido —
  // duplicando cada leitura/gravação e alargando a janela de race do auto-save.
  get: (rfqId) => api.get(`/rfqs/${rfqId}/quotation/`),
  update: (rfqId, data) => api.put(`/rfqs/${rfqId}/quotation/`, data),
  getCalc: (rfqId) => api.get(`/rfqs/${rfqId}/quotation/calc`),
  preview: (data) => api.post('/calculate/preview', data),
  priceValidationPreview: (data) => api.post('/calculate/price-validation-preview', data),
};

// ============ CLIENTS ============
export const clientAPI = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  // Indicadores de saúde financeira (perfil Financeiro / Comercial / Admin).
  updateFinancial: (id, data) => api.put(`/clients/${id}/financial`, data),
  // Documentos do cliente (relatório financeiro).
  listDocuments: (id) => api.get(`/clients/${id}/documents`),
  uploadDocument: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clients/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadDocument: (clientId, documentId) =>
    api
      .get(`/clients/${clientId}/documents/${documentId}`, { responseType: 'blob' })
      .then((res) => res.data),
  deleteDocument: (clientId, documentId) =>
    api.delete(`/clients/${clientId}/documents/${documentId}`),
};

// ============ CONFIG ============
export const configAPI = {
  getMachines: () => api.get('/config/machines'),
  createMachine: (data) => api.post('/config/machines', data),
  updateMachine: (id, data) => api.put(`/config/machines/${id}`, data),
  getAlloys: () => api.get('/config/alloys'),
  createAlloy: (data) => api.post('/config/alloys', data),
  updateAlloy: (id, data) => api.put(`/config/alloys/${id}`, data),
  // Histórico de versões de preço de uma liga (gráfico + escolha na cotação).
  getAlloyVersions: (id) => api.get(`/config/alloys/${id}/versions`),
  getFinishingRates: (includeInactive = false, category = 'all') =>
    api.get('/config/finishing-rates', {
      params: { include_inactive: includeInactive, category },
    }),
  createFinishingRate: (data) => api.post('/config/finishing-rates', data),
  updateFinishingRate: (id, data) => api.put(`/config/finishing-rates/${id}`, data),
  deleteFinishingRate: (id) => api.delete(`/config/finishing-rates/${id}`),
  getBoxTypes: (includeInactive = false) =>
    api.get('/config/box-types', { params: { include_inactive: includeInactive } }),
  createBoxType: (data) => api.post('/config/box-types', data),
  updateBoxType: (id, data) => api.put(`/config/box-types/${id}`, data),
  getPalletCost: () => api.get('/config/pallet-cost'),
  updatePalletCost: (data) => api.put('/config/pallet-cost', data),
  // Fornecedores — usados como select no campo "Fornecedor" do card de
  // Subcontratação. ``supplierType`` filtra a lista pela categoria da
  // linha (machining, surface_treatment, painting, other).
  getSuppliers: (includeInactive = false, supplierType = '') =>
    api.get('/config/suppliers', {
      params: { include_inactive: includeInactive, supplier_type: supplierType },
    }),
  createSupplier: (data) => api.post('/config/suppliers', data),
  updateSupplier: (id, data) => api.put(`/config/suppliers/${id}`, data),
  // Setores de atividade (dropdown do cliente) — catálogo parametrizável.
  getActivitySectors: (includeInactive = false) =>
    api.get('/config/activity-sectors', { params: { include_inactive: includeInactive } }),
  createActivitySector: (data) => api.post('/config/activity-sectors', data),
  updateActivitySector: (id, data) => api.put(`/config/activity-sectors/${id}`, data),
  // Funções de contacto (dropdown na tabela de contactos do cliente).
  getContactFunctions: (includeInactive = false) =>
    api.get('/config/contact-functions', { params: { include_inactive: includeInactive } }),
  createContactFunction: (data) => api.post('/config/contact-functions', data),
  updateContactFunction: (id, data) => api.put(`/config/contact-functions/${id}`, data),
  // Histórico de tempos de ciclo (sugestão no card VA Fundição).
  getCycleTimes: () => api.get('/config/cycle-times'),
  createCycleTime: (data) => api.post('/config/cycle-times', data),
  updateCycleTime: (id, data) => api.put(`/config/cycle-times/${id}`, data),
  deleteCycleTime: (id) => api.delete(`/config/cycle-times/${id}`),
  importCycleTimes: (file, replace = false) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/config/cycle-times/import', form, {
      params: { replace },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  suggestCycleTime: (params) => api.get('/config/cycle-times/suggestion', { params }),
  getGlobal: () => api.get('/config/global'),
  updateGlobal: (key, value) => api.put(`/config/global/${key}`, null, { params: { value } }),
  getPnLConfig: () => api.get('/config/pnl-config'),
  updatePnLConfig: (data) => api.put('/config/pnl-config', data),
  getCostStructureActive: () => api.get('/config/cost-structure-versions/active'),
  listCostStructureVersions: () => api.get('/config/cost-structure-versions'),
  createCostStructureVersion: (data) => api.post('/config/cost-structure-versions', data),
  activateCostStructureVersion: (id) => api.post(`/config/cost-structure-versions/${id}/activate`),
};

// ============ USERS ============
export const userAPI = {
  list: () => api.get('/users'),
  listAssignable: () => api.get('/users/assignable'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  deactivate: (id) => api.delete(`/users/${id}`),
};

// ============ ROLES ============
export const roleAPI = {
  list: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
};

// ============ DASHBOARD ============
// O endpoint suporta ?period=last_7d|last_30d|last_90d|ytd|last_12m|custom.
// Default last_30d (decisão tomada com a Inês: dashboard com viés
// operacional). Para "custom" é preciso passar `from` e `to` (ISO 8601 ou
// YYYY-MM-DD). Aceitamos duas assinaturas:
//   - `dashboardAPI.get('last_30d')`
//   - `dashboardAPI.get({ id: 'custom', from: '2026-01-01', to: '2026-02-15' })`
export const dashboardAPI = {
  get: (period) => {
    if (!period) return api.get('/dashboard/');
    if (typeof period === 'string') {
      return api.get('/dashboard/', { params: { period } });
    }
    const params = { period: period.id || 'last_30d' };
    if (period.from) params.from = period.from;
    if (period.to) params.to = period.to;
    return api.get('/dashboard/', { params });
  },
};

// ============ NOTIFICATIONS ============
export const notificationAPI = {
  // Barra final: rota definida em "/" no backend (evita 307).
  list: () => api.get('/notifications/'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  remove: (id) => api.delete(`/notifications/${id}`),
  removeAll: () => api.delete('/notifications/'),
};

export default api;
